import { Capacitor } from '@capacitor/core';
import { API_BASE_URL } from '../api';
import { LOCAL_WEB_VERSION } from '../buildInfo';
import {
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP_DISPLAY,
  SUPPORT_WHATSAPP_E164,
} from '../constants/contact';
import { isStorageAvailable } from './safeStorage';

function platformLabel() {
  try {
    if (Capacitor.isNativePlatform()) {
      return `native-${Capacitor.getPlatform()}`;
    }
  } catch {
    // ignore
  }
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'web-ios';
    if (/Android/i.test(ua)) return 'web-android';
  }
  return 'web';
}

function shortUserAgent() {
  try {
    return String(navigator.userAgent || '').slice(0, 220);
  } catch {
    return '';
  }
}

function apiHost() {
  try {
    return new URL(API_BASE_URL).host;
  } catch {
    return String(API_BASE_URL || '');
  }
}

function schoolCodeFromUsername(username) {
  const u = String(username || '').trim();
  if (!u.includes('_')) return '';
  return u.split('_')[0] || '';
}

function connectionInfo() {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return '';
    const parts = [c.effectiveType, c.type, c.downlink != null ? `${c.downlink}Mbps` : null].filter(Boolean);
    return parts.join(' / ');
  } catch {
    return '';
  }
}

function screenInfo() {
  try {
    const w = window.screen?.width;
    const h = window.screen?.height;
    const dpr = window.devicePixelRatio;
    if (!w || !h) return '';
    return `${w}x${h}${dpr ? ` @${dpr}x` : ''}`;
  } catch {
    return '';
  }
}

function pageUrl() {
  try {
    return String(window.location?.href || '').slice(0, 200);
  } catch {
    return '';
  }
}

function serviceWorkerState() {
  try {
    if (!('serviceWorker' in navigator)) return 'unsupported';
    return navigator.serviceWorker.controller ? 'controlling' : 'no-controller';
  } catch {
    return '';
  }
}

function languageInfo() {
  try {
    return String(navigator.language || document.documentElement?.lang || '');
  } catch {
    return '';
  }
}

function onlineState() {
  try {
    return navigator.onLine ? 'online' : 'offline';
  } catch {
    return 'unknown';
  }
}

/**
 * Full support report for a failed login (never includes password).
 */
export function buildLoginErrorReport({
  role,
  username,
  errorMessage,
  errorCode,
  httpStatus,
  axiosCode,
  rawMessage,
  useMalayalam,
}) {
  const whenUtc = new Date().toISOString();
  const whenLocal = new Date().toString();
  const uname = String(username || '').trim();
  const schoolCode = schoolCodeFromUsername(uname);
  const conn = connectionInfo();
  const screen = screenInfo();
  const sw = serviceWorkerState();
  const lang = languageInfo();

  const lines = [
    '===== NMhelper login error report =====',
    `Time (UTC): ${whenUtc}`,
    `Time (device): ${whenLocal}`,
    `Role selected: ${role || '(none)'}`,
    `Username: ${uname || '(empty)'}`,
    schoolCode ? `School code (from username): ${schoolCode}` : null,
    `Error shown: ${errorMessage || '(none)'}`,
    errorCode ? `Error code: ${errorCode}` : 'Error code: (none)',
    httpStatus != null ? `HTTP status: ${httpStatus}` : 'HTTP status: (no response)',
    axiosCode ? `Axios/network code: ${axiosCode}` : null,
    rawMessage && rawMessage !== errorMessage ? `Raw message: ${rawMessage}` : null,
    `App version: ${LOCAL_WEB_VERSION}`,
    `API host: ${apiHost()}`,
    `API base: ${API_BASE_URL}`,
    `Platform: ${platformLabel()}`,
    `Online: ${onlineState()}`,
    `Storage OK: ${isStorageAvailable() ? 'yes' : 'no'}`,
    `UI language: ${useMalayalam ? 'ml' : 'en'}`,
    lang ? `Browser language: ${lang}` : null,
    conn ? `Connection: ${conn}` : null,
    screen ? `Screen: ${screen}` : null,
    sw ? `Service worker: ${sw}` : null,
    `Page: ${pageUrl() || '(n/a)'}`,
    `UA: ${shortUserAgent()}`,
    '',
    `Support WhatsApp: ${SUPPORT_WHATSAPP_DISPLAY}`,
    `Support email: ${SUPPORT_EMAIL}`,
    '',
    'Password is NOT included. Please help fix login for this school.',
    '=======================================',
  ].filter((line) => line != null && line !== false);

  return lines.join('\n');
}

export function buildLoginWhatsAppUrl(reportText) {
  const intro = 'NMhelper login problem — report below:\n\n';
  return `https://wa.me/${SUPPORT_WHATSAPP_E164}?text=${encodeURIComponent(intro + reportText)}`;
}

export function buildLoginMailtoUrl(reportText) {
  const subject = 'NMhelper login error report';
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(reportText)}`;
}

export async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Share sheet when available (native / some browsers). */
export async function shareLoginReport(reportText, title = 'NMhelper login error report') {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text: reportText, dialogTitle: title });
      return true;
    }
  } catch {
    // fall through to Web Share
  }
  try {
    if (navigator.share) {
      await navigator.share({ title, text: reportText });
      return true;
    }
  } catch (err) {
    if (err?.name === 'AbortError') return false;
  }
  return false;
}

/** Friendlier network/login messages (no password). */
export function formatLoginFailureMessage({
  err,
  labels,
  role,
  username,
}) {
  const data = err?.response?.data;
  const code = data?.code;
  const serverMsg = data?.error;
  const status = err?.response?.status;
  const uname = String(username || '').trim();
  const looksLikeTeacherId = uname.includes('_');

  if (code === 'WRONG_ROLE' && data?.hintRole) {
    const tpl =
      labels.loginWrongRoleHint ||
      'This username belongs to {role}. Select {role} and try again.';
    return String(tpl).split('{role}').join(data.hintRole);
  }
  if (code === 'INVALID_PASSWORD') {
    return (
      labels.loginBadPasswordHint ||
      'Username found, but password is wrong. Ask your school head to reset the teacher password.'
    );
  }
  if (code === 'USER_NOT_FOUND') {
    if (role === 'teacher' && !looksLikeTeacherId) {
      return (
        labels.loginTeacherIdFormatHint ||
        'Teacher login needs full ID like schoolcode_2A (school code + _ + class/pen).'
      );
    }
    if (role === 'head' && looksLikeTeacherId) {
      return (
        labels.loginHeadNotTeacherIdHint ||
        'That looks like a teacher ID. Select Teacher, or use the head username without _class.'
      );
    }
    return serverMsg || labels.loginUserNotFound || 'Username not found. Check spelling and role.';
  }
  if (code === 'AMBIGUOUS_USERNAME') {
    return serverMsg;
  }
  if (code === 'SUBSCRIPTION_EXPIRED' || status === 403) {
    return serverMsg || labels.subscriptionExpiredShort || 'Subscription issue. Contact admin.';
  }

  const isNetwork =
    !err?.response &&
    (err?.code === 'ERR_NETWORK' ||
      err?.code === 'ECONNABORTED' ||
      /network|cors|failed to fetch|timeout/i.test(String(err?.message || '')));

  if (isNetwork) {
    return (
      labels.loginNetworkError ||
      'Cannot reach the server (network error). Check internet, try mobile data, then send the report below.'
    );
  }

  return serverMsg || err?.message || 'Login failed.';
}
